// Basic
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
// Store & actions
import useRootOrganizationStore from '@/app/store/rootOrganization';
import usePlansStore from '@/app/store/modules/plan';
import actionsPlans from '../../../api/actionsPlans';
// Components
import HeaderTitle from '@/common-utils/components/renders/modal/components/headers/HeaderTitle/HeaderTitle';
import PlansTable from '../../tables/PlansTable';
import FooterForm from '@/common-utils/components/renders/modal/components/footers/FooterForm/FooterForm';
import Entity from '@/common-utils/components/renders/entity/Entity';
import { Modal, ModalBody, ModalContent } from '@/common-utils/components/ui/modal';
// Common
import getInitialValueForRowSelected from '@/common-utils/components/renders/dataTable/utils/GetInitialValueForRowSelected';
import { useToast } from '@/common-utils/hooks/use-toast';
// Lang
import { nsPlan } from '@/app/i18n/namespaces';
import setFiltersEventName from '@/common-utils/components/renders/dataTable/utils/setFiltersEventName';

/**
 * @param root0
 * @param root0.isOpen
 * @param root0.setIsOpen
 * @param root0.currentOrganizationId
 * @returns {JSX} ModalAddPlansToOrganization
 */
function ModalAddPlansToOrganization({ isOpen, setIsOpen, currentOrganizationId }) {
    // Store
    const rootOrganization = useRootOrganizationStore(s => s.rootOrganization);
    const setLaunchRefetch = usePlansStore(s => s.storeRefetchPlans);

    // State
    const [disableAction, setDisableAction] = useState(true);
    const [plansSelected, setPlansSelected] = useState(getInitialValueForRowSelected());

    // Toast
    const { toast } = useToast();

    // Hooks
    useEffect(() => {
        setDisableAction(!Object.keys(plansSelected.rowIds).length);
    }, [plansSelected]);

    // Api calls
    const onSuccess = () => {
        setIsOpen(false);
        setLaunchRefetch(true);
        toast({
            description: <Entity namespace={nsPlan} entity='modal.add_allowed_clients.success' />,
            variant: 'success'
        });
    };
    const onError = (error) => {
        const errorCode = error.response?.data?.errorCode ?? error.code;
        const addClientErrorCode = errorCode === 'CLIENT_ALREADY_ALLOWED' ? 'SUBSCRIPTION_PLAN_ALREADY_ALLOWED' : 'UNKNOWN_ERROR';
        setIsOpen(false);
        toast({
            description: <Entity namespace={nsPlan} entity={`toast.error.${addClientErrorCode}`} />,
            variant: 'danger'
        });
    };
    const addAllowedClients = actionsPlans.addAllowedClients(onSuccess, onError);

    return (
        <Modal open={isOpen} onOpenChange={setIsOpen}>
            <ModalContent className='max-w-6xl'>
                <HeaderTitle title='modal.add_existing_plan.title' namespace={nsPlan} />
                <ModalBody className='modal-max-height overflow-auto'>
                    <PlansTable
                        filtersEventName={setFiltersEventName('plans', true)}
                        singleSelection={false}
                        rowsSelected={plansSelected}
                        setRowsSelected={setPlansSelected}
                    />
                </ModalBody>
                <FooterForm
                    onSubmit={
                        () => {
                            Object.keys(plansSelected.rowIds).forEach((planId) => {
                                addAllowedClients.mutate({
                                    organizationId: rootOrganization.uuid,
                                    planId: planId,
                                    allowedClientsIds: [currentOrganizationId]
                                });
                            });
                        }
                    }
                    isSubmitting={addAllowedClients.isLoading}
                    isSubmitDisabled={disableAction}
                />
            </ModalContent>
        </Modal>
    );
}

ModalAddPlansToOrganization.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    setIsOpen: PropTypes.func.isRequired,
    currentOrganizationId: PropTypes.string.isRequired
};

export default ModalAddPlansToOrganization;